import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, CameraIcon, Flashlight, FlashlightOff, RefreshCw, X } from "lucide-react";
import { parseNFeAccessKey } from "../../shared/nfe-access-key";

type ScannerKind = "BARCODE" | "QR_CODE";

export function FiscalCodeScanner({
  open,
  onDetected,
  onPhoto,
  onCancel
}: {
  open: boolean;
  onDetected: (value: string, kind: ScannerKind) => void;
  onPhoto: (file: File) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannerControlsRef = useRef<{ stop: () => void } | null>(null);
  const timerRef = useRef<number | null>(null);
  const lockedRef = useRef(false);
  const [error, setError] = useState("");
  const [torch, setTorch] = useState(false);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");
  const [scanning, setScanning] = useState(false);

  const stopMedia = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    scannerControlsRef.current?.stop();
    scannerControlsRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    stopMedia();
    setScanning(false);
    setTorch(false);
  }, [stopMedia]);

  useEffect(() => () => stopMedia(), [stopMedia]);

  useEffect(() => {
    if (!open) {
      stopMedia();
      return;
    }
    let disposed = false;
    lockedRef.current = false;
    const start = async () => {
      setError("");
      if (!window.isSecureContext && location.hostname !== "127.0.0.1" && location.hostname !== "localhost") {
        setError("A câmera exige HTTPS no celular. Use o acesso HTTPS do CCE Gestão ou digite a chave.");
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Este navegador não disponibiliza acesso à câmera. Use a chave digitada ou o envio de uma foto.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: facingMode }, width: { ideal: 1920 }, height: { ideal: 1080 }, focusMode: "continuous" } as MediaTrackConstraints
        });
        if (disposed) return stream.getTracks().forEach((track) => track.stop());
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const acceptResult = (value: unknown) => {
          const raw = String(value || "").trim();
          const kind: ScannerKind = /^https:\/\//i.test(raw) ? "QR_CODE" : "BARCODE";
          const keyCandidate = kind === "QR_CODE" ? raw.match(/\d{44}/)?.[0] : raw;
          if (!raw || (kind !== "QR_CODE" && (!keyCandidate || !parseNFeAccessKey(keyCandidate).valid))) return;
          lockedRef.current = true;
          navigator.vibrate?.(120);
          stop();
          onDetected(raw, kind);
        };

        const Detector = (window as any).BarcodeDetector;
        if (Detector) {
          const detector = new Detector({ formats: ["code_128", "qr_code", "itf", "ean_13"] });
          setScanning(true);
          const detect = async () => {
            if (disposed || lockedRef.current || !videoRef.current) return;
            try {
              const results = await detector.detect(videoRef.current);
              for (const result of results || []) {
                acceptResult(result.rawValue);
                if (lockedRef.current) return;
              }
            } catch {
              // Um quadro sem leitura é esperado; a captura continua controladamente.
            }
            timerRef.current = window.setTimeout(detect, 160);
          };
          void detect();
          return;
        }

        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        if (disposed || !videoRef.current) return;
        const reader = new BrowserMultiFormatReader(undefined, {
          delayBetweenScanAttempts: 100,
          delayBetweenScanSuccess: 500
        });
        scannerControlsRef.current = await reader.decodeFromStream(stream, videoRef.current, (result) => {
          if (result && !lockedRef.current) acceptResult(result.getText());
        });
        setScanning(true);
      } catch (caught) {
        setError(caught instanceof Error && caught.name === "NotAllowedError"
          ? "Permissão da câmera negada. Autorize-a no navegador ou use a chave digitada."
          : "Não foi possível iniciar o leitor. Tente alternar a câmera, fotografar o DANFE ou digitar a chave.");
      }
    };
    void start();
    return () => { disposed = true; stopMedia(); };
  }, [facingMode, onDetected, open, stop, stopMedia]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean };
    if (!capabilities?.torch) return setError("A lanterna não está disponível nesta câmera.");
    const next = !torch;
    try {
      await track.applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] });
      setTorch(next);
    } catch {
      setError("Não foi possível controlar a lanterna neste aparelho.");
    }
  };

  if (!open) return null;
  return <div className="fiscal-scanner" role="dialog" aria-modal="true" aria-label="Escanear código da nota fiscal">
    <div className="scanner-toolbar">
      <div><Camera size={19}/><strong>Escanear NF-e / NFC-e</strong></div>
      <button type="button" onClick={onCancel} aria-label="Cancelar leitura"><X size={20}/></button>
    </div>
    <div className="scanner-viewport">
      <video ref={videoRef} playsInline muted aria-label="Imagem ao vivo da câmera"/>
      <div className="scanner-target"><span/><span/><span/><span/></div>
      {scanning && <small>Aponte para o código de barras do DANFE ou QR Code da NFC-e</small>}
    </div>
    {error && <p className="scanner-error">{error}</p>}
    <div className="scanner-actions">
      <button type="button" onClick={() => void toggleTorch()} disabled={!scanning}>{torch ? <FlashlightOff size={17}/> : <Flashlight size={17}/>} {torch ? "Desligar lanterna" : "Lanterna"}</button>
      <button type="button" onClick={() => setFacingMode((current) => current === "environment" ? "user" : "environment")}><RefreshCw size={17}/> Alternar câmera</button>
      <label><CameraIcon size={17}/> Fotografar DANFE<input type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) onPhoto(file); }}/></label>
    </div>
  </div>;
}
